import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../../supabaseClient";
import {
  fetchDefaultPayoutTableId,
  listOrganizationPayoutTables,
  loadPayoutTable,
  savePayoutTable,
} from "../../organizations/PayoutTables/useOrgPayoutTables";

export const useTournamentPayoutTable = (tournamentId) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tournament, setTournament] = useState(null);
  const [table, setTable] = useState(null);
  const [loadedPayoutData, setLoadedPayoutData] = useState([]);
  const [organizationTables, setOrganizationTables] = useState([]);
  const [defaultPayoutTableId, setDefaultPayoutTableId] = useState(null);
  const [isPayoutFinalized, setIsPayoutFinalized] = useState(false);

  const applyLoadedTable = (loaded) => {
    if (!loaded) {
      setTable(null);
      setLoadedPayoutData([]);
      return;
    }
    setTable(loaded.table);
    setLoadedPayoutData(loaded.payoutData || []);
  };

  const refresh = useCallback(async () => {
    if (!tournamentId) return;
    setLoading(true);
    setError("");
    try {
      const { data: tournamentData, error: tournamentError } = await supabase
        .from("tournaments")
        .select("id, name, organizationID, payoutTableId")
        .eq("id", tournamentId)
        .single();

      if (tournamentError) throw tournamentError;
      setTournament(tournamentData);

      const { data: verificationStates, error: verificationError } =
        await supabase
          .from("verificationStates")
          .select("isVerified")
          .eq("tournamentId", tournamentId)
          .eq("entityType", "payouts")
          .is("entityId", null);

      if (verificationError) {
        console.warn("Could not fetch payout lock status:", verificationError);
      }
      setIsPayoutFinalized(Boolean(verificationStates?.[0]?.isVerified));

      const orgTables = await listOrganizationPayoutTables(
        tournamentData.organizationID
      );
      setOrganizationTables(orgTables);
      setDefaultPayoutTableId(
        await fetchDefaultPayoutTableId(tournamentData.organizationID)
      );

      if (tournamentData.payoutTableId) {
        applyLoadedTable(await loadPayoutTable(tournamentData.payoutTableId));
      } else {
        applyLoadedTable(null);
      }
    } catch (err) {
      console.error(err);
      setError("Could not load this payout table.");
      applyLoadedTable(null);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const assignTable = async (organizationTableId) => {
    if (!tournamentId || !organizationTableId) return;
    const { error: updateError } = await supabase
      .from("tournaments")
      .update({ payoutTableId: organizationTableId })
      .eq("id", tournamentId);
    if (updateError) throw updateError;
    await refresh();
  };

  const saveCurrent = async ({
    name: nextName,
    description: nextDescription,
    payoutData: nextData,
    recipe,
  }) => {
    if (isPayoutFinalized) {
      throw new Error("Payouts are locked.");
    }
    if (!table) throw new Error("Pick a payout table first.");
    const saved = await savePayoutTable({
      currentTable: table,
      name: nextName,
      description: nextDescription,
      payoutData: nextData,
      recipe,
    });
    setTable(saved);
    setLoadedPayoutData(nextData.map((row) => ({ ...row })));
    return saved;
  };

  const isFrozenCopy = Boolean(
    table?.frozenForTournamentId &&
      table.frozenForTournamentId === tournamentId
  );

  return {
    loading,
    error,
    setError,
    tournament,
    table,
    loadedPayoutData,
    organizationTables,
    defaultPayoutTableId,
    isPayoutFinalized,
    isFrozenCopy,
    isFollowingOrganization: Boolean(table) && !isFrozenCopy,
    refresh,
    assignTable,
    saveCurrent,
  };
};
